import React, { useState, useMemo, useContext, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import TailwindWrapper from "../../TailwindWrapper/TailwindWrapper";
import EMPProgressBar from "../../../shared/components/EMPProgressBar/EMPProgressBar";
import ScrollableItemList from "../../../shared/components/ScrollableItemList/ScrollableItemList";
import { Box, Button, Typography } from "@mui/material";
import { UserContext } from "../../../shared/context/userContext";
import { IS_FORD_BRAND } from "../../../utils/EmpUtil";
import {
  findDealerInGroupByContext,
  mapDealerGroupRowToAddressPayload,
} from "../../../shared/utils/dealerGroupSelection";
import GetStartedContent from "../GetStartedContent/GetStartedContent";
import OnboardingEnrollment from "../OnboardingEnrollment/OnboardingEnrollment";
import OnboardingPaymentProcessorOnlineDeposits from "../OnboardingPaymentProcessorOnlineDeposits/OnboardingPaymentProcessorOnlineDeposits";
import OnboardingEntitlements from "../OnboardingEntitlements/OnboardingEntitlements";
import OnboardingFinancing from "../OnboardingFinancing/OnboardingFinancing";
import OnboardingTradeIn from "../OnboardingTradeIn/OnboardingTradeIn";
import OnboardingVehiclePricing from "../OnboardingVehiclePricing/OnboardingVehiclePricing";
import OnboardingAccessoryPricing from "../OnboardingAccessoryPricing/OnboardingAccessoryPricing";
import OnboardingThirdPartyFinancing from "../OnboardingThirdPartyFinancing/OnboardingThirdPartyFinancing";
import OnboardingProtectionPlans from "../OnboardingProtectionPlans/OnboardingProtectionPlans";
import OnboardingTierThreeBuyNow from "../OnboardingTierThreeBuyNow/OnboardingTierThreeBuyNow";
import OnboardingAdvertisedPlanPricing from "../OnboardingAdvertisedPlanPricing/OnboardingAdvertisedPlanPricing";
import DealerOnboardingClient from "../../../clients/DealerOnboardingClient";
import {
  DEALER_ONBOARDING_PROGRESS_QUERY_KEY,
  isOnboardingLandingTabCompleted,
  isEntitlementsSubstepCompleted,
  ENTITLEMENTS_SUBSTEP_KDM,
  ENTITLEMENTS_SUBSTEP_VEHICLE_PRICING,
  ENTITLEMENTS_SUBSTEP_ACCESSORY_PRICING,
  ENTITLEMENTS_SUBSTEP_CUSTOMER_HANDLING,
} from "../../../shared/utils/onboardingProgressUtil";

const proximaRegularFontFamily =
  '"ProximaNovaRegular", "Lincoln Proxima Nova", Arial, sans-serif';

const proximaSemiboldFontFamily =
  '"ProximaNovaSemibold", "Lincoln Proxima Nova Semibold", Arial, sans-serif';

const proximaBoldFontFamily =
  '"ProximaNovaBold", "Lincoln Proxima Nova Bold", Arial, sans-serif';

const STEP_KEYS = [
  "enrollment",
  "entitlements",
  "financing",
  "tradeIn",
  "paymentProcessorOnlineDeposits",
  "vehiclePricing",
  "accessoryPricing",
  "thirdPartyFinancing",
  "protectionPlans",
  "tierThreeBuyNow",
  "advertisedPlanPricing",
];

const DealerOnboardingLandingPage = () => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const [filterType, setFilterType] = useState("all");
  const [selectedStep, setSelectedStep] = useState(
    "onboardingSteps-getStarted"
  );
  const isFordBrand = IS_FORD_BRAND(user?.brand);
  const brandName = isFordBrand ? "Ford" : "Lincoln";

  const progressRequest = useMemo(() => {
    const dealerCommonId = user?.commonId || user?.commonDealerId;
    if (!dealerCommonId) return null;

    const matchingDealer = findDealerInGroupByContext(
      user?.dealerGroup,
      dealerCommonId,
      user
    );
    const addressPayload = mapDealerGroupRowToAddressPayload(matchingDealer);
    const hasFordDealer = (user?.dealerGroup || []).some((dealer) =>
      dealer?.commonId?.endsWith("F")
    );
    const hasLincolnDealer = (user?.dealerGroup || []).some((dealer) =>
      dealer?.commonId?.endsWith("L")
    );
    const dualDealer = hasFordDealer && hasLincolnDealer;

    const dealerInfo = {
      dealerCommonId,
      dealerName:
        user?.dealerName ||
        user?.name ||
        matchingDealer?.dealerName ||
        matchingDealer?.name,
      brand: user?.brand,
      dualDealer,
      market: user?.market,
      paCode: user?.dealerId || matchingDealer?.paCode,
      salesCode: user?.salesCode || matchingDealer?.salesCode,
      dealerType: user?.dealerType || matchingDealer?.dealerType,
      ...(user?.userId ? { userId: user.userId } : {}),
      ...addressPayload,
    };

    if (!dealerInfo.dealerName || !dealerInfo.brand || !dealerInfo.market) {
      return null;
    }

    return { dealerCommonId, dealerInfo };
  }, [
    user,
    user?.brand,
    user?.commonDealerId,
    user?.commonId,
    user?.dealerGroup,
    user?.dealerId,
    user?.dealerName,
    user?.dealerType,
    user?.market,
    user?.name,
    user?.salesCode,
    user?.userId,
  ]);

  const { data: progressData } = useQuery({
    queryKey: [
      DEALER_ONBOARDING_PROGRESS_QUERY_KEY,
      progressRequest?.dealerCommonId,
    ],
    queryFn: () =>
      DealerOnboardingClient.getDealerOnboardingProgress(
        progressRequest.dealerCommonId,
        progressRequest.dealerInfo
      ),
    enabled: !!progressRequest,
    select: (response) => response?.data ?? null,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const progressSteps = progressData?.steps;
  const isEnrollmentComplete = useMemo(
    () =>
      isOnboardingLandingTabCompleted(
        progressSteps,
        "onboardingSteps-enrollment"
      ),
    [progressSteps]
  );

  const backendProgressPercent = (() => {
    const n = Number(progressData?.progressPercent);
    return Number.isNaN(n) ? 0 : n;
  })();

  const progressPercent = isEnrollmentComplete ? backendProgressPercent : 0;
  const progressPercentLabel = `${Math.round(progressPercent)}%`;

  const styles = {
    container: { borderTop: "1px solid #e5e5e5", backgroundColor: "#ffffff" },
    pageTitleWrapper: {
      marginBottom: "40px",
    },
    contentGrid: {
      columnGap: "40px",
      rowGap: "24px",
    },
    progressBar: {
      width: "100%",
      maxWidth: "408px",
      marginBottom: "16px",
      boxSizing: "border-box",
    },
    progressBarRow: {
      display: "flex",
      alignItems: "center",
      width: "100%",
      gap: "16px",
      boxSizing: "border-box",
    },
    progressBarTrack: {
      flex: 1,
      minWidth: 0,
    },
    progressPercentText: {
      color: "var(--color-text-onlight-moderate-default, #333)",
      fontFamily: proximaSemiboldFontFamily,
      fontSize: "var(--subtitle-semibold-font-size, 20px)",
      fontStyle: "normal",
      fontWeight: "var(--subtitle-semibold-font-weight, 600)",
      lineHeight: "var(--subtitle-semibold-line-height, 28px)",
      whiteSpace: "nowrap",
    },
    filterContainer: {
      display: "flex",
      width: "100%",
      maxWidth: "408px",
      height: "46px",
      boxSizing: "border-box",
      overflow: "hidden",
      borderRadius: "var(--radius-md, 8px)",
      padding: "4px",
      background: "var(--segmented-control-onlight-background, #EDEFF0)",
      marginBottom: "16px",
      gap: "10px",
    },
    stepsList: { width: "408px" },
    pageTitle: {
      color: "#000",
      fontFamily: proximaRegularFontFamily,
      fontSize: "var(--headline-1-small-regular-font-size, 36px)",
      fontStyle: "normal",
      fontWeight: "var(--headline-1-small-regular-font-weight, 400)",
      lineHeight: "var(--headline-1-small-regular-line-height, 44px)",
      margin: 0,
    },
  };

  const showAllButtonTextStyle = {
    color: "var(--color-text-onlight-interactive, #485B65)",
    fontFamily: proximaBoldFontFamily,
    fontSize: "var(--body-2-bold-font-size, 14px)",
    fontStyle: "normal",
    fontWeight: "var(--body-2-bold-font-weight, 700)",
    lineHeight: "var(--body-2-bold-line-height, 20px)",
  };

  const notCompletedButtonTextStyle = {
    color: "var(--color-text-onlight-strong, #000)",
    fontFamily: proximaRegularFontFamily,
    fontSize: "var(--body-2-medium-font-size, 14px)",
    fontStyle: "normal",
    fontWeight: "var(--body-2-medium-font-weight, 400)",
    lineHeight: "var(--body-2-medium-line-height, 20px)",
  };

  const getFilterButtonStyle = (isActive, buttonType) => ({
    flex: "1 1 0",
    minWidth: 0,
    maxWidth: "none",
    height: "36px",
    boxSizing: "border-box",
    borderRadius:
      "4px var(--radius-sm, 4px) var(--radius-sm, 4px) var(--radius-sm, 4px)",
    backgroundColor: isActive ? "#FFFFFF" : "transparent",
    border: isActive
      ? "1px solid var(--color-stroke-onlight-interactive, #485B65)"
      : "none",
    textTransform: "none",
    padding: "8px 12px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    ...(buttonType === "showAll"
      ? showAllButtonTextStyle
      : notCompletedButtonTextStyle),
  });

  const onboardingSteps = useMemo(
    () => [
      {
        id: "onboardingSteps-getStarted",
        title: t("DealerOnboarding.content.onboardingSteps.getStarted.title"),
        description: t(
          "DealerOnboarding.content.onboardingSteps.getStarted.description"
        ),
        isActive: true,
        disabled: false,
      },
      ...STEP_KEYS.map((key) => {
        const stepId = `onboardingSteps-${key}`;
        const completed = isOnboardingLandingTabCompleted(
          progressSteps,
          stepId
        );

        return {
          id: stepId,
          title: t(`DealerOnboarding.content.onboardingSteps.${key}.title`),
          description: t(
            `DealerOnboarding.content.onboardingSteps.${key}.description`
          ),
          isActive: false,
          disabled: false,
          hasStatus: true,
          statusLabel: completed
            ? t("DealerOnboarding.steps.completed", "Complete")
            : t("DealerOnboarding.steps.incomplete", "Incomplete"),
          statusColor: completed ? "success" : "warning",
        };
      }),
    ],
    [t, progressSteps]
  );

  const filteredSteps = useMemo(() => {
    const enrollmentGatedSteps = isEnrollmentComplete
      ? onboardingSteps
      : onboardingSteps.filter(
          (step) =>
            step.id === "onboardingSteps-getStarted" ||
            step.id === "onboardingSteps-enrollment"
        );

    if (filterType === "notCompleted") {
      return enrollmentGatedSteps.filter((step) => {
        if (step.id === "onboardingSteps-getStarted") {
          return true;
        }
        if (isOnboardingLandingTabCompleted(progressSteps, step.id)) {
          return false;
        }
        return true;
      });
    }
    return enrollmentGatedSteps;
  }, [onboardingSteps, filterType, progressSteps, isEnrollmentComplete]);

  useEffect(() => {
    if (filteredSteps.some((s) => s.id === selectedStep)) {
      return;
    }
    const fallbackId = filteredSteps[0]?.id ?? "onboardingSteps-getStarted";
    setSelectedStep(fallbackId);
  }, [filteredSteps, selectedStep]);

  const handleStepClick = (stepId) => {
    setSelectedStep(stepId);
  };

  const handleFilterClick = (filter) => {
    setFilterType(filter);
  };

  const renderStepContent = () => {
    switch (selectedStep) {
      case "onboardingSteps-getStarted":
        return (
          <GetStartedContent
            user={user}
            brandName={brandName}
            progressRequest={progressRequest}
          />
        );
      case "onboardingSteps-enrollment":
        return (
          <OnboardingEnrollment user={user} progressRequest={progressRequest} />
        );
      case "onboardingSteps-entitlements":
        return (
          <OnboardingEntitlements
            user={user}
            brandName={brandName}
            kdmCompleted={isEntitlementsSubstepCompleted(
              progressSteps,
              ENTITLEMENTS_SUBSTEP_KDM
            )}
            vehiclePricingCompleted={isEntitlementsSubstepCompleted(
              progressSteps,
              ENTITLEMENTS_SUBSTEP_VEHICLE_PRICING
            )}
            accessoriesCompleted={isEntitlementsSubstepCompleted(
              progressSteps,
              ENTITLEMENTS_SUBSTEP_ACCESSORY_PRICING
            )}
            customerHandlingCompleted={isEntitlementsSubstepCompleted(
              progressSteps,
              ENTITLEMENTS_SUBSTEP_CUSTOMER_HANDLING
            )}
          />
        );
      case "onboardingSteps-financing":
        return (
          <OnboardingFinancing
            savedRouteOneId={progressData?.preferences?.routeOneId}
          />
        );
      case "onboardingSteps-tradeIn":
        return (
          <OnboardingTradeIn
            savedTradeInPreference={
              progressData?.preferences?.tradeInPreference
            }
          />
        );
      case "onboardingSteps-paymentProcessorOnlineDeposits":
        return (
          <OnboardingPaymentProcessorOnlineDeposits
            savedStripePreference={progressData?.preferences?.stripePreference}
            progressSteps={progressSteps}
          />
        );
      case "onboardingSteps-vehiclePricing":
        return (
          <OnboardingVehiclePricing
            user={user}
            progressRequest={progressRequest}
            progressSteps={progressSteps}
            savedVehiclePricingPreference={
              progressData?.preferences?.vehiclePricingPreference
            }
          />
        );
      case "onboardingSteps-accessoryPricing":
        return <OnboardingAccessoryPricing />;
      case "onboardingSteps-thirdPartyFinancing":
        return (
          <OnboardingThirdPartyFinancing progressRequest={progressRequest} />
        );
      case "onboardingSteps-protectionPlans":
        return (
          <OnboardingProtectionPlans
            hasSavedRouteOneId={Boolean(progressData?.preferences?.routeOneId)}
          />
        );
      case "onboardingSteps-tierThreeBuyNow":
        return (
          <OnboardingTierThreeBuyNow
            savedBuyNowPreference={progressData?.preferences?.buyNowPreference}
          />
        );
      case "onboardingSteps-advertisedPlanPricing":
        return (
          <OnboardingAdvertisedPlanPricing
            savedAdvertisedPlanPricingPreference={
              progressData?.preferences?.advertisedPlanPricingPreference
            }
          />
        );
      default:
        return null;
    }
  };

  return (
    <TailwindWrapper>
      <Box
        className="tw-min-h-screen tw-px-4 tw-py-4 1440:tw-px-14"
        style={styles.container}
      >
        <Box className="tw-max-w-7xl tw-mx-auto tw-mt-8">
          <Box style={styles.pageTitleWrapper}>
            <Typography
              component="h1"
              data-testid="dealer-onboarding-main-title"
              style={styles.pageTitle}
            >
              {t("DealerOnboarding.mainTitle", {
                brand: brandName,
              })}
            </Typography>
          </Box>

          <Box
            className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-12"
            style={styles.contentGrid}
          >
            <Box className="tw-col-span-1 lg:tw-col-span-4">
              <Box className="tw-bg-white tw-rounded-lg">
                <Box style={styles.progressBar}>
                  <Box style={styles.progressBarRow}>
                    <Box style={styles.progressBarTrack}>
                      <EMPProgressBar
                        progress={progressPercent}
                        showPercentage={false}
                      />
                    </Box>

                    <span
                      style={styles.progressPercentText}
                      data-testid="dealer-onboarding-progress-percent"
                    >
                      {progressPercentLabel}
                    </span>
                  </Box>
                </Box>

                <Box className="tw-flex tw-mb-6" style={styles.filterContainer}>
                  <Button
                    variant={filterType === "all" ? "outlined" : "text"}
                    onClick={() => handleFilterClick("all")}
                    size="small"
                    data-testid="filter-show-all"
                    style={getFilterButtonStyle(
                      filterType === "all",
                      "showAll"
                    )}
                  >
                    {t("DealerOnboarding.filters.showAll", "Show All")}
                  </Button>

                  <Button
                    variant={
                      filterType === "notCompleted" ? "outlined" : "text"
                    }
                    onClick={() => handleFilterClick("notCompleted")}
                    size="small"
                    data-testid="filter-not-completed"
                    style={getFilterButtonStyle(
                      filterType === "notCompleted",
                      "notCompleted"
                    )}
                  >
                    {t(
                      "DealerOnboarding.filters.notCompleted",
                      "Not Completed"
                    )}
                  </Button>
                </Box>

                <ScrollableItemList
                  items={filteredSteps}
                  onItemClick={(id) => handleStepClick(id)}
                  selectedItem={selectedStep}
                  maxHeight="calc(100vh - 260px)"
                />
              </Box>
            </Box>

            <Box className="tw-col-span-1 lg:tw-col-span-8">
              {renderStepContent()}
            </Box>
          </Box>
        </Box>
      </Box>
    </TailwindWrapper>
  );
};

export default DealerOnboardingLandingPage;
